import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useFetch } from '../hooks/useFetch'
import { SCHAFFERHOLZ_REPORT } from '../data/schafferholzReport'
import { SectionView, REPORT_PART_LABELS, REPORT_PART_ORDER } from '@rb/ui'
import type { AnalysisReport, ProjectDetailData } from '@rb/ui'
import styles from './ProjectReport.module.css'

interface ProjectResponse {
  project: ProjectDetailData
  analysis_results: unknown | null
}

function formatDate(): string {
  return new Date().toLocaleDateString('de-AT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

export default function ProjectReport() {
  const { id } = useParams<{ id: string }>()
  const { data, loading, error } = useFetch<ProjectResponse>(`/projects/${id}`)

  const [autoPrint] = useState(
    () => new URLSearchParams(window.location.search).get('print') === '1'
  )

  useEffect(() => {
    const handler = () => window.dispatchEvent(new Event('resize'))
    window.addEventListener('beforeprint', handler)
    return () => window.removeEventListener('beforeprint', handler)
  }, [])

  useEffect(() => {
    if (!autoPrint || loading) return
    const t = setTimeout(() => window.print(), 900)
    return () => clearTimeout(t)
  }, [autoPrint, loading])

  const project = data?.project
  const ar = data?.analysis_results
  const report: AnalysisReport = ar && project
    ? {
        cover: {
          period: new Date(project.updated_at).toLocaleDateString('de-AT', {
            day: 'numeric', month: 'long', year: 'numeric',
          }),
        },
        analyse:      ar.analysis   ?? [],
        vorschlag:    ar.proposal   ?? [],
        roi:          ar.roi        ?? [],
        finanzierung: ar.financing  ?? [],
      }
    : SCHAFFERHOLZ_REPORT

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingState}>Wird geladen…</div>
      </div>
    )
  }

  if (error || !project) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState}>Projekt nicht gefunden.</div>
      </div>
    )
  }

  const cover = report.cover
  const projectName = project.name || '(Ohne Titel)'

  return (
    <div className={styles.page}>
      {/* ── Running header — position:fixed in print, repeats on every page ── */}
      <header className={styles.reportHeader}>
        <img
          src="/rb-logo-light-mode.svg"
          alt="Reisenbauer Solutions"
          className={styles.headerLogo}
        />
        <span className={styles.headerProjectName}>
          {projectName}&ensp;—&ensp;Energieanalyse
        </span>
      </header>

      {/* ── Print button — screen only ──────────────────────────────────────── */}
      <button className={styles.printBtn} onClick={() => window.print()}>
        Drucken / Als PDF speichern
      </button>

      {/* ── Cover page (page 1) ─────────────────────────────────────────────
           In print: position:relative + z-index:100 + full-paper white
           background masks the fixed header/footer (z-index:10) on page 1
           only. On pages 2+ the cover element is gone so fixed elements
           render normally. break-after:page forces content onto page 2. ─── */}
      <div className={styles.coverPage}>
        <img
          src="/rb-logo-light-mode.svg"
          alt="Reisenbauer Solutions"
          className={styles.coverLogo}
        />
        <div className={styles.coverBody}>
          <p className={styles.coverLabel}>Energieanalyse</p>
          <h1 className={styles.coverTitle}>{projectName}</h1>
          {cover?.subtitle && <p className={styles.coverSubtitle}>{cover.subtitle}</p>}
          {cover?.period && <p className={styles.coverPeriod}>{cover.period}</p>}
        </div>
        <div className={styles.coverFooterBlock}>
          <p className={styles.coverCredit}>Erstellt von Reisenbauer Solutions</p>
          <p className={styles.coverDate}>{formatDate()}</p>
        </div>
      </div>

      {/* ── Report content — pages 2+ ───────────────────────────────────────── */}
      <main className={styles.content}>
        {REPORT_PART_ORDER.map((part) => {
          const sections = report[part]
          if (!sections || sections.length === 0) return null
          return (
            <div key={part} className={styles.part}>
              <h2 className={styles.partHeading}>{REPORT_PART_LABELS[part]}</h2>
              {sections.map((section, si) => (
                <div key={si} className={styles.sectionWrapper}>
                  <h3 className={styles.sectionTitle}>{section.section_title}</h3>
                  <SectionView section={section} />
                </div>
              ))}
            </div>
          )
        })}

        <div className={styles.disclaimer}>
          <p>
            Dieses Dokument dient der Unterstützung digitaler Investitionsentscheidungen für
            Energie. Es stellt keine Rechts-, Steuer- oder Finanzberatung dar. Alle Analysen
            basieren auf den bereitgestellten Daten. Förderprogramme unterliegen Änderungen —
            aktuelle Konditionen bei den Förderstellen prüfen. Amortisationsberechnungen sind
            Prognosen. Alle Angaben ohne Gewähr.
          </p>
        </div>
      </main>

      {/* ── Running footer — position:fixed in print, repeats on every page ── */}
      <footer className={styles.reportFooter}>
        <span className={styles.footerLeft}>
          <span className={styles.footerAddress}>
            Reisenbauer Solutions GmbH&ensp;·&ensp;Handelskai 265&ensp;·&ensp;TOP 328&ensp;·&ensp;1020 Wien
          </span>
          <span className={styles.footerLegal}>
            Handelsgericht Wien FN 531864 d&ensp;|&ensp;St.Nr. 799/2832&ensp;|&ensp;UID ATU 75453616
            &ensp;·&ensp;IBAN AT15 3258 5000 0710 2429&ensp;|&ensp;BIC RLNWATWWOBG
          </span>
        </span>
        <span className={styles.footerRight}>reisenbauer.solutions</span>
      </footer>
    </div>
  )
}
